import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useWorkspaceKeys, type WorkspaceKeys } from "../app/current-user.tsx";
import { applyWorkspaceWrite, type WorkspaceWrite } from "./workspace-cache.ts";

interface WorkspaceMutationOptions<TData, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  /** The records the answer fills or deletes; everything else in the workspace is refreshed. */
  cache?: (keys: WorkspaceKeys, data: TData, variables: TVariables) => WorkspaceWrite;
}

/**
 * Every workspace write: on success it updates the cache (see `applyWorkspaceWrite`)
 * without awaiting the refresh, so `isPending` ends when the server answers. Per-call
 * `onSuccess`/`onError` passed to `mutate` run as usual.
 */
export const useWorkspaceMutation = <TData, TVariables>({
  mutationFn,
  cache,
}: WorkspaceMutationOptions<TData, TVariables>): UseMutationResult<TData, Error, TVariables> => {
  const queryClient = useQueryClient();
  const keys = useWorkspaceKeys();
  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      applyWorkspaceWrite(queryClient, keys.all, cache?.(keys, data, variables) ?? {});
    },
  });
};
